/**
 * StepProgress Component
 * Visual indicator for research agent pipeline steps
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { AgentStepType, AgentStepStatus } from '@/types/research.types';

interface StepProgressProps {
  steps: Array<{
    type: AgentStepType;
    status: AgentStepStatus;
    label: string;
    data?: any;
  }>;
  onStepPress?: (type: AgentStepType) => void;
}

export const StepProgress: React.FC<StepProgressProps> = ({ steps, onStepPress }) => {
  const getIcon = (status: AgentStepStatus) => {
    switch (status) {
      case 'complete':
        return { name: 'checkmark-circle' as const, color: Colors.status.success };
      case 'in_progress':
        return { name: 'hourglass' as const, color: Colors.accent.primary };
      case 'error':
        return { name: 'close-circle' as const, color: Colors.status.error };
      default:
        return { name: 'ellipse-outline' as const, color: Colors.text.tertiary };
    }
  };

  return (
    <View style={styles.container}>
      {steps.map((step, index) => {
        const icon = getIcon(step.status);
        const isClickable = step.status === 'complete' && onStepPress;

        return (
          <TouchableOpacity
            key={step.type}
            style={styles.step}
            onPress={() => isClickable && onStepPress(step.type)}
            disabled={!isClickable}
          >
            <Ionicons name={icon.name} size={24} color={icon.color} />
            <View style={styles.stepContent}>
              <Text style={styles.stepLabel}>{step.label}</Text>
              {step.status === 'in_progress' && step.data?.progress && (
                <Text style={styles.stepProgress}>{step.data.progress}</Text>
              )}
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  stepContent: {
    marginLeft: 12,
    flex: 1,
  },
  stepLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  stepProgress: {
    fontSize: 13,
    color: Colors.text.secondary,
    marginTop: 2,
  },
});
